import { useEffect, useRef, useState } from 'react';

import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { horizontalListSortingStrategy, SortableContext } from '@dnd-kit/sortable';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import { useWindowSize } from '@components/component-helpers';
import { FilterQueryData, FiltersConjunctionType } from '@components/filters';
import { IconButton } from '@components/icon-button';
import { IconAddPlus } from '@components/icons/add-plus';
import { Stack } from '@components/stack';
import { useIsMobile } from '@hooks/use-is-mobile';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { EmptyApplicationResult } from 'components/EmptyApplicationResult';
import { PipelineCard } from 'components/pipeline-card';
import { useModal } from 'features/modals';
import { usePermissions } from 'features/permissions';
import { useManageJobAccess } from 'hooks/use-manage-job-access';
import { useStageEntryJourneyPrompt } from 'integrations/journeys/job-triggers/use-stage-entry-journey';
import type { IApplication, IJob, IJobStage } from 'models/types';

import { DraggableStage, Stage } from './stage';

type DraggingState =
  | { type: 'stage'; index: number }
  | { type: 'application'; id: number; stageId: number }
  | null;

export const Pipeline = observer<{
  job: IJob;
  queryData: FilterQueryData;
  conjunction: FiltersConjunctionType;
  filtersOpen: boolean;
  hasJourneysBanner?: boolean;
}>(function Pipeline(props) {
  const { job, queryData, conjunction, filtersOpen, hasJourneysBanner = false } = props;

  const [height, setHeight] = useState<number>(0);
  const ref = useRef<HTMLDivElement>(null);
  const windowSize = useWindowSize();
  const { hasRolePermission } = usePermissions();
  const [draggingItem, setDraggingItem] = useState<DraggingState>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    })
  );
  const stages = job.jobStages as Array<IJobStage>;
  const stageIds = stages.map((js) => String(js.id));
  const isMobile = useIsMobile();
  const { showModal } = useModal();
  const { promptOrChainStageEntryJourney } = useStageEntryJourneyPrompt(job.id);

  useEffect(() => {
    return job.resetStages;
  }, [job]);

  useEffect(() => {
    job.setSearchQueryData(queryData, conjunction);
    job.loadStages();
    job.loadJobApplicationsCount();
  }, [job, job.settings.state, queryData, conjunction]);

  useEffect(() => {
    if (ref.current) {
      setHeight(windowSize.height - ref.current.getBoundingClientRect().y);
    }
  }, [windowSize, filtersOpen, queryData, hasJourneysBanner]);

  let draggingStage: IJobStage | null = null;
  let draggingApplication: IApplication | null = null;

  if (draggingItem) {
    if (draggingItem.type === 'stage') {
      draggingStage = stages[draggingItem.index];
    }

    if (draggingItem.type === 'application') {
      const stage = stages.find((stage) => stage.id === draggingItem.stageId);
      if (stage) {
        draggingApplication = stage.applications.find(
          (application: IApplication) => application.id === draggingItem.id
        );
      }
    }
  }

  const isLoading = job.detailLoading || stages.some((stage) => stage.state === 'loading');
  const hasApplications = stages.some((stage) => stage.applications.length > 0);
  const hasFilters = Object.keys(queryData).length > 0;

  const hasManageAccess = useManageJobAccess(job.createdById?.id);
  const canDragStages = !isMobile && hasManageAccess;
  const canDragApplications = !isMobile && hasRolePermission('application:move');

  function onDragStart(event: DragStartEvent) {
    const dragType = event.active.data.current?.type;
    if (!dragType) return;
    if (dragType === 'stage') {
      if (!canDragStages) return;
      setDraggingItem({
        type: 'stage',
        index: event.active.data.current?.sortable.index,
      });
    } else if (dragType === 'card') {
      if (!canDragApplications) return;
      setDraggingItem({
        type: 'application',
        id: Number(event.active.id.toString().replace('a--', '')),
        stageId: event.active.data.current?.stageId,
      });
    }
  }

  async function onDragEnd(event: DragEndEvent) {
    if (!draggingItem) return;
    setDraggingItem(null);

    if (draggingItem.type === 'stage') {
      if (!canDragStages) return;
      const dragType = event.active.data.current?.type;
      if (!dragType) return;

      if (!event.over || event.active.id === event.over.id) {
        return;
      }

      const movingToIndex = event.over.data.current?.sortable.index;
      await job.moveStage(draggingItem.index, movingToIndex);
    } else {
      if (!canDragApplications) return;
      if (!event.over || !draggingApplication) return;

      if (event.active.data.current?.stageId !== Number(event.over.id)) {
        const targetStageId = Number(event.over.id);
        await draggingApplication.changeStage(targetStageId, showModal);
        // changeStage is optimistic and reverts jobStageId on failure, so a
        // matching id after the await means the move actually persisted.
        if (draggingApplication.jobStageId === targetStageId) {
          promptOrChainStageEntryJourney([draggingApplication.id], targetStageId, {
            job,
            isApplicationHired: draggingApplication.isHired,
          });
        }
      }
    }
  }

  function onDragCancel() {
    setDraggingItem(null);
  }

  return (
    <DndContext
      sensors={sensors}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
      autoScroll={{
        threshold: {
          x: 0.1,
          y: 0.1,
        },
      }}
    >
      <DragOverlay>
        {draggingStage && <Stage isOverlay stage={draggingStage} />}
        {draggingApplication && (
          <PipelineCardOverlay>
            <PipelineCard isOverlay application={draggingApplication} />
          </PipelineCardOverlay>
        )}
      </DragOverlay>
      <Container ref={ref} style={{ height }}>
        <SortableContext items={stageIds} strategy={horizontalListSortingStrategy}>
          {stages.map((stage: IJobStage) => {
            if (canDragStages) {
              return (
                <DraggableStage
                  canDragApplications={canDragApplications}
                  isDragging={draggingStage ? draggingStage.id === stage.id : false}
                  key={stage.id}
                  stage={stage}
                />
              );
            }
            return <Stage canDragApplications={canDragApplications} key={stage.id} stage={stage} />;
          })}
        </SortableContext>

        {hasManageAccess && (
          <IconButton data-cy="new-stage-button" icon={<IconAddPlus />} onClick={job.addStage} />
        )}
        {!hasApplications && !isLoading && (
          <NoApplicantsContainer>
            <EmptyApplicationResult
              isFiltered={hasFilters}
              hasSearch={Boolean(job.settings.searchStr)}
            />
          </NoApplicantsContainer>
        )}
      </Container>
    </DndContext>
  );
});

const NoApplicantsContainer = styled.div({
  position: 'absolute',
  left: 0,
  top: 80,
  width: '100% !important',
});

const PipelineCardOverlay = styled.div({
  border: `${pxToRem(1)} solid ${theme.color.purple40}`,
  margin: pxToRem(-1),
  borderRadius: pxToRem(8),
  boxShadow: theme.shadow.high,
  a: {
    cursor: 'grab',
  },
});

const Container = styled(Stack).attrs({
  space: 4,
  orientation: 'horizontal',
})({
  position: 'relative',
  flexGrow: 1,
  padding: pxToRem(24),
  overflowY: 'hidden',
  overflowX: 'auto',

  '> div': {
    overflow: 'hidden',
    height: '100%',
    flexShrink: 0,
    width: pxToRem(296),
  },
});
